import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import setAnimationTimeout from '../../../core/utils/setAnimationTimeout';
import ActivitiesChapterI, { type ActivityEntry } from './ActivityChapter.interface';
import EntryLayout from './SectionLayout';
import { DEFAULT_EASE } from '../../../rise/constants/transition';
import useOpenSection from '../../helpers/use-open-section';
import { getEntries } from './helpers';
import { get, isNumber } from 'lodash';
import { secToMs } from '../../../core/utils/transforms';
import { DEFAULT_CAROUSEL_ITEM_DURATION_S } from './ActivityChapter.constants';

export default function ActivityCarouselScene({
                                                  entries: sections = [],
                                                  title = 'Move with us',
                                                  media,
                                              }: ActivitiesChapterI) {
    const openSection = useOpenSection();
    const [activeIndex, setActiveIndex] = useState(openSection === null ? 0 : openSection + 1);
    const cache = useRef([]);

    useEffect(() => {
        if (openSection !== null) {
            return;
        }

        const sectionDurationS = calculateItemDuration(sections[activeIndex]);

        return setAnimationTimeout(() => {
            return setActiveIndex(i => {
                const next = ++i;
                if (next >= sections?.length) {
                    return 0;
                }

                return next;
            });
        }, secToMs(sectionDurationS));
    }, [activeIndex, sections.length, openSection]);

    const isIntro = useMemo(() => activeIndex === 0, [activeIndex]);

    const activeSectionComponent = useMemo(() => {
        let cachedItem = cache.current[activeIndex];
        if (cachedItem) {
            return cachedItem;
        }

        const entry = sections[activeIndex];

        cachedItem = entry ? (
            <EntryLayout
                allEntries={sections}
                key={activeIndex}
                entry={entry}
            />
        ) : null;

        cache.current.push[cachedItem];

        return cachedItem;
    }, [activeIndex, isIntro]);

    return (
        <AnimatePresence>
            <motion.div
                key={activeIndex}
                style={{
                    position: 'absolute',
                    width: '100vw',
                    height: '100vh',
                    fontFamily: 'HelveticaNowDisplay',
                    fontWeight: 500,
                }}
                initial={isIntro ? { y: 0 } : { y: '100vh' }}
                animate={{ x: 0, y: 0 }}
                exit={{
                    y: 0,
                    x: 0,
                    transitionEnd: {
                        display: 'none',
                    },
                }}
                transition={{
                    duration: 1,
                    ease: DEFAULT_EASE,
                }}
            >
                {activeSectionComponent}
            </motion.div>
        </AnimatePresence>
    );
}

export function calculateItemDuration(entryItem: ActivityEntry) {
    if (!entryItem) {
        return 0;
    }

    let duration = DEFAULT_CAROUSEL_ITEM_DURATION_S;
    if (entryItem?.has_qr_code && entryItem?.qr_code?.variant !== 'corner') {
        const qrDuration = get(entryItem, 'qr_code.media.force_duration', DEFAULT_CAROUSEL_ITEM_DURATION_S);
        duration += Number(qrDuration);
    }

    return duration;
}

export function calculateDuration(props: ActivitiesChapterI, lockedSectionIdx: number) {
    const entries = getEntries(props);

    if (isNumber(lockedSectionIdx)) {
        return calculateItemDuration(entries[lockedSectionIdx]);
    }

    return entries.reduce((acc, curr) => {
        return acc + calculateItemDuration(curr);
    }, 0);
}
